import React, { useState, useEffect } from 'react';
import { CustomTable, Loader, Search, SuccessModal, ConfirmationPopup, Pagination } from '@ohif/ui';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '@custom/store/store';
import DoctorDiscountForm from './DoctorDiscountForm';
import { servicesManager } from '../../../app/src/App';
import getDoctorDiscountApi from '@custom/rtk-apis/billing/doctorDiscount/getDoctorDiscount';
import addDoctorDiscountApi from '@custom/rtk-apis/billing/doctorDiscount/addDoctorDiscount';
import editDoctorDiscountApi from '@custom/rtk-apis/billing/doctorDiscount/editDoctorDiscount';
import deleteDoctorDiscountApi from '@custom/rtk-apis/billing/doctorDiscount/deleteDoctorDiscount';
import { clearAddDoctorDiscount } from '@custom/store/slices/billing/doctorDiscount/addDoctorDiscount';
import { cleareditDoctorDiscount } from '@custom/store/slices/billing/doctorDiscount/editDoctorDiscount';
import { cleardeleteDoctorDiscount } from '@custom/store/slices/billing/doctorDiscount/deleteDoctorDiscount';
import { cleargetDoctorDiscount } from '@custom/store/slices/billing/doctorDiscount/getDoctorDiscount';

interface ConsultationProps {
  activeTab: string;
}

const DoctorDiscount = (props: ConsultationProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const getDoctorDiscount = useSelector((state: RootState) => state?.getDoctorDiscountResponse);
  const addDoctorDiscount = useSelector((state: RootState) => state?.addDoctorDiscountResponse);
  const deleteDoctorDiscount = useSelector((state: RootState) => state?.deleteDoctorDiscountResponse);
  const editDoctorDiscount = useSelector((state: RootState) => state?.editDoctorDiscountResponse);

  const { uiNotificationService } = servicesManager.services;
  const [tableData, setTableData] = useState([]);
  const [searchInputValue, setSearchInputValue] = useState('');
  const [pgNumber, setPgNumber] = useState(1);
  const [pgLimit, setPgLimit] = useState(30);
  const [totalPg, setTotalPg] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDiscountForm, setShowDiscountForm] = useState(false);
  const [editDoctor, setEditDoctor] = useState(null);
  const [success, setSuccess] = useState(false);
  const [msg, setMsg] = useState('');
  const [edit, setEdit] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const columns = [
    { key: 'doctorName', title: 'Doctor Name' },
    { key: 'investigationDiscount', title: 'Investigation Discount(%)', sort: true },
    { key: 'investigationHikePrice', title: 'Investigation Price Hike(%)' },
    { key: 'actions', title: 'Action' },
  ];

  const defaultInitialValues = {
    doctorName: '',
    investigationDiscount: '',
    investigationHikePrice: '',
    priceFields: [{
      modality: '',
      studyRegion: '',
      defaultPrice: '',
      priceDiscount: '',
      priceHike: ''
    }]
  };

  const [initialValues, setInitialValues] = useState(defaultInitialValues);

  useEffect(() => {
    if (props.activeTab === 'doctorDiscount') {
      dispatch(getDoctorDiscountApi({ search: searchQuery, page: pgNumber, limit: pgLimit }));
    }
  }, [dispatch, props.activeTab, searchQuery, pgNumber, pgLimit]);

  useEffect(() => {
    if (getDoctorDiscount.status === 'success') {
      setTableData(getDoctorDiscount?.result?.doctors.map((doctor) => ({
        ...doctor,
        investigationDiscount: doctor.investigationDiscount > 0
          ? `${doctor.investigationDiscount}%`
          : "--",
        investigationHikePrice: doctor.investigationHikePrice > 0
          ? `${doctor.investigationHikePrice}%`
          : "--",
      })));
      setTotalPg(getDoctorDiscount?.result?.pagination?.totalDoctors);
      dispatch(cleargetDoctorDiscount());
    }
  }, [getDoctorDiscount.status]);

  useEffect(() => {
    if (addDoctorDiscount?.status === 'success') {
      setMsg('Doctor discount added successfully.');
      setSuccess(true);
      setShowDiscountForm(false);
      setTimeout(() => {
        dispatch(getDoctorDiscountApi({ search: searchQuery, page: 1, limit: pgLimit }));
      }, 1500);
    } else if (addDoctorDiscount?.status === 'error') {
      uiNotificationService.show({
        title: 'Doctor Discount Error',
        message: addDoctorDiscount?.msg || 'An error occurred while adding the doctor discount.',
        type: 'error',
      });
      setShowDiscountForm(false);
    }
    return () => {
      dispatch(clearAddDoctorDiscount());
    }
  }, [addDoctorDiscount, dispatch, uiNotificationService]);

  useEffect(() => {
    if (editDoctorDiscount?.status === 'success') {
      setMsg('Doctor discount updated successfully.');
      setSuccess(true);
      setShowDiscountForm(false);
      setTimeout(() => {
        dispatch(getDoctorDiscountApi({ search: searchQuery, page: 1, limit: pgLimit }));
      }, 1500);
    } else if (editDoctorDiscount?.status === 'error') {
      uiNotificationService.show({
        title: 'Edit Error',
        message: editDoctorDiscount?.msg || 'An error occurred while updating the doctor discount.',
        type: 'error',
      });
      setShowDiscountForm(false);
    }
    return () => {
      dispatch(cleareditDoctorDiscount());
    }
  }, [editDoctorDiscount, dispatch, uiNotificationService]);

  useEffect(() => {
    if (deleteDoctorDiscount?.status === 'success') {
      setMsg('Doctor discount deleted successfully.');
      setSuccess(true);
      setTimeout(() => {
        dispatch(getDoctorDiscountApi({ search: searchQuery, page: 1, limit: pgLimit }));
      }, 1500);
    } else if (deleteDoctorDiscount?.status === 'error') {
      uiNotificationService.show({
        title: 'Delete Error',
        message: deleteDoctorDiscount?.msg || 'An error occurred while deleting the doctor discount.',
        type: 'error',
      });
    }
    setShowDeleteModal(false);
    setDeleteId(null);
    return () => {
      dispatch(cleardeleteDoctorDiscount());
    }
  }, [deleteDoctorDiscount, dispatch, uiNotificationService]);

  const submitForm = async (values) => {
    const hospitalId = sessionStorage.getItem('hospitalId') || localStorage.getItem('hospitalId');
    console.log(values);
    const payload = {
      hospitalId,
      ...values
    };

    if (edit && editDoctor) {
      await dispatch(editDoctorDiscountApi(payload));
    } else {
      await dispatch(addDoctorDiscountApi(payload));
    }
  };

  const handleAddCharges = () => {
    setEditDoctor(null);
    setEdit(false);
    setInitialValues(defaultInitialValues);
    setShowDiscountForm(true);
  };

  const handleDeleteConfirm = async () => {
    if (deleteId) {
      const payload = { doctorId: deleteId };
      await dispatch(deleteDoctorDiscountApi(payload));
    }
  };

  const handleDelete = (item) => {
    setDeleteId(item.id);
    setShowDeleteModal(true);
  };

  const handleCancel = () => {
    setShowDeleteModal(false);
    setDeleteId(null);
  };

  const actions = [
    {
      id: 'editDoctorDiscount',
      label: 'Edit',
      iconName: 'edit',
      onClick: (item) => {
        setEditDoctor(item);
        setEdit(true);
        setInitialValues(item);
        setShowDiscountForm(true);
      },
    },
    {
      id: 'deleteDoctorDiscount',
      label: 'Delete',
      iconName: 'delete',
      onClick: (item) => handleDelete(item),
    },
  ];

  const searchClick = (searchText) => {
    setSearchInputValue(searchText);
    setSearchQuery(searchText);
    setPgNumber(1);
    dispatch(getDoctorDiscountApi({ search: searchText, page: 1, limit: pgLimit }));
  };

  return (
    <>
      <div className='page__body'>
          <div className="tableWrap">
          {!showDiscountForm ? (
            <>
              <div className="tableWrap__head">
                <div className="formRow formRow__3 m_0 w_100 formRow--flexEnd">
                  <div className="formCol">
                    <Search
                      text="Search"
                      onSearchClick={searchClick}
                      value={searchInputValue}
                      setValue={setSearchInputValue}
                    />
                  </div>
                </div>
                {props.activeTab === 'doctorDiscount' && (
                  <div className="btn__group">
                    <button className="btn btn__white" onClick={handleAddCharges}>
                      <svg width="15" height="14" viewBox="0 0 15 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M7.63509 1.16699V12.8337M1.80176 7.00033H13.4684"
                          stroke="#150823"
                          strokeWidth="1.67"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      Add Doctor Discount/Hike
                    </button>
                  </div>
                )}
              </div>
              <div className="tableContainer">
                <CustomTable
                  columns={columns}
                  data={tableData}
                  actions={actions}
                  searchQuery={searchQuery}
                  from='doctorDiscountHike'
                />
              </div>
            </>
          ) : (
            <DoctorDiscountForm
              editDoctor={editDoctor}
              initialValues={initialValues}
              submitForm={submitForm}
              edit={edit}
              setEdit={setEdit}
              setShowDiscountForm={setShowDiscountForm}
              isSubmitting={addDoctorDiscount?.loading || editDoctorDiscount?.loading}
            />
          )}
        </div>
      </div>
      <SuccessModal
        isSuccessModalOpen={success}
        title={msg}
        handleCloseModal={() => setSuccess(false)}
      />
      <Loader isLoaderOpen={getDoctorDiscount?.loading || addDoctorDiscount?.loading || deleteDoctorDiscount?.loading || editDoctorDiscount?.loading} />
      <ConfirmationPopup
        isOpen={showDeleteModal}
        title=""
        message="Are you sure you want to delete this Doctor Discount?"
        onConfirm={handleDeleteConfirm}
        onCancel={handleCancel}
      />
      {tableData.length > 0 && !showDiscountForm && (
        <Pagination onChangePage={setPgNumber} onChangePerPage={setPgLimit} totalPage={totalPg} currentPage={pgNumber} perPage={pgLimit} />
      )}
    </>
  );
};

export default DoctorDiscount;
